import { zodResolver } from "@hookform/resolvers/zod";
import type { JSX } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { apiErrorMessage } from "@/api/errors";
import type { components } from "@/api/schema";
import { toast } from "@/shared/lib/toast";
import { Modal } from "@/shared/ui/modal";

import { useCloneToMarketplaces } from "../api/use-clone-to-marketplaces";
import { useCreatePromotion } from "../api/use-create-promotion";
import { toIso } from "../lib/datetime-local";
import { emptyMultiChannelTargets, multiChannelTargetsSchema } from "../lib/multi-channel-schema";

import { PrimaryButton, SecondaryButton } from "./buttons";
import { SellerSelect } from "./SellerSelect";

type CloneTarget = components["schemas"]["CloneTarget"];

interface CampaignOption {
  id: string;
  name: string;
}

interface PromotionFormModalProps {
  campaigns: CampaignOption[];
  defaultCampaignId?: string;
  /** Called with the FIRST created promotion's id — the Builder always opens on it. */
  onCreated: (id: string) => void;
  onClose: () => void;
}

const FIELD_CLASS =
  "h-10 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50";
const LABEL_CLASS = "mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]";
const ERROR_CLASS = "mt-1 text-xs text-[var(--intent-danger-fg)]";

const CHANNELS = [
  { key: "shopee", label: "Shopee" },
  { key: "lazada", label: "Lazada" },
  { key: "tiktok", label: "TikTok" },
] as const;

const shellSchema = z
  .object({
    campaign_id: z.string().trim().min(1, "promotion:create.err_campaign_required"),
    name: z.string().trim().min(1, "promotion:builder.err_name_required"),
    start_at: z.string().min(1, "promotion:create.err_start_required"),
    end_at: z.string().min(1, "promotion:create.err_end_required"),
    rule_mode: z.enum(["all", "break"]),
    channels: multiChannelTargetsSchema,
  })
  .superRefine((values, ctx) => {
    if (Date.parse(values.end_at) <= Date.parse(values.start_at)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["end_at"],
        message: "promotion:builder.err_dates",
      });
    }
  });

type FormValues = z.infer<typeof shellSchema>;

export function PromotionFormModal({
  campaigns,
  defaultCampaignId,
  onCreated,
  onClose,
}: PromotionFormModalProps): JSX.Element {
  const { t } = useTranslation("promotion");
  const create = useCreatePromotion();
  const cloneToMarketplaces = useCloneToMarketplaces();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(shellSchema),
    defaultValues: {
      campaign_id: defaultCampaignId ?? "",
      name: "",
      start_at: "",
      end_at: "",
      rule_mode: "all",
      channels: emptyMultiChannelTargets,
    },
  });

  function onSubmit(values: FormValues): void {
    const checked = CHANNELS.filter((c) => values.channels[c.key].checked);
    const [first, ...rest] = checked;
    if (!first) return; // schema guarantees at least one; guard keeps TS happy
    create.mutate(
      {
        campaign_id: values.campaign_id,
        name: values.name,
        marketplace: first.key,
        seller_id: values.channels[first.key].seller_id,
        start_at: toIso(values.start_at),
        end_at: toIso(values.end_at),
        rule_mode: values.rule_mode,
      },
      {
        onSuccess: (created) => {
          if (!created?.id) return;
          if (rest.length === 0) {
            onClose();
            onCreated(created.id);
            return;
          }
          const targets: CloneTarget[] = rest.map((c) => ({
            marketplace: c.key,
            seller_id: values.channels[c.key].seller_id,
          }));
          cloneToMarketplaces.mutate(
            { id: created.id, targets },
            {
              onSuccess: () => {
                onClose();
                onCreated(created.id);
              },
              onError: (err) => toast.error(apiErrorMessage(err)),
            },
          );
        },
        onError: (err) => toast.error(apiErrorMessage(err)),
      },
    );
  }

  const isPending = create.isPending || cloneToMarketplaces.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={t("create.title")}
      subtitle={t("create.subtitle")}
      footer={
        <>
          <SecondaryButton
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="promotion-create-cancel"
          >
            {t("create.cancel")}
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            form="promotion-create-form"
            disabled={isPending}
            data-testid="promotion-create-submit"
          >
            {t("create.submit")}
          </PrimaryButton>
        </>
      }
    >
      <form
        id="promotion-create-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid="promotion-create-form"
        className="space-y-4"
      >
        <div>
          <label htmlFor="promotion-campaign" className={LABEL_CLASS}>
            {t("create.campaign_label")}
          </label>
          <select
            id="promotion-campaign"
            data-testid="promotion-create-campaign"
            disabled={isPending}
            className={FIELD_CLASS}
            {...register("campaign_id")}
          >
            <option value="">{t("create.campaign_placeholder")}</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {errors.campaign_id && (
            <p className={ERROR_CLASS} data-testid="promotion-create-campaign-error">
              {t(errors.campaign_id.message ?? "create.err_campaign_required")}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="promotion-name" className={LABEL_CLASS}>
            {t("create.name_label")}
          </label>
          <input
            id="promotion-name"
            type="text"
            data-testid="promotion-create-name"
            placeholder={t("create.name_placeholder")}
            disabled={isPending}
            className={FIELD_CLASS}
            {...register("name")}
          />
          {errors.name && (
            <p className={ERROR_CLASS} data-testid="promotion-create-name-error">
              {t(errors.name.message ?? "builder.err_name_required")}
            </p>
          )}
        </div>

        <div>
          <label className={LABEL_CLASS}>{t("create.marketplace_label")}</label>
          <div className="space-y-2">
            {CHANNELS.map((c) => (
              <div key={c.key}>
                <div className="flex items-center gap-3">
                  <label className="flex w-28 items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      data-testid={`promotion-create-channel-${c.key}`}
                      disabled={isPending}
                      {...register(`channels.${c.key}.checked`)}
                    />
                    {c.label}
                  </label>
                  <Controller
                    control={control}
                    name={`channels.${c.key}.seller_id`}
                    render={({ field }) => (
                      <SellerSelect
                        id={`promotion-create-seller-${c.key}`}
                        testId={`promotion-create-seller-${c.key}`}
                        marketplace={c.key}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isPending}
                        className={FIELD_CLASS}
                      />
                    )}
                  />
                </div>
                {errors.channels?.[c.key]?.seller_id && (
                  <p className={ERROR_CLASS} data-testid={`promotion-create-seller-${c.key}-error`}>
                    {t(errors.channels[c.key]?.seller_id?.message ?? "builder.err_seller_required")}
                  </p>
                )}
              </div>
            ))}
          </div>
          {errors.channels?.shopee?.message && (
            <p className={ERROR_CLASS} data-testid="promotion-create-channels-error">
              {t(errors.channels.shopee.message)}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="promotion-start" className={LABEL_CLASS}>
              {t("create.start_label")}
            </label>
            <input
              id="promotion-start"
              type="datetime-local"
              data-testid="promotion-create-start"
              disabled={isPending}
              className={FIELD_CLASS}
              {...register("start_at")}
            />
            {errors.start_at && (
              <p className={ERROR_CLASS} data-testid="promotion-create-start-error">
                {t(errors.start_at.message ?? "create.err_start_required")}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="promotion-end" className={LABEL_CLASS}>
              {t("create.end_label")}
            </label>
            <input
              id="promotion-end"
              type="datetime-local"
              data-testid="promotion-create-end"
              disabled={isPending}
              className={FIELD_CLASS}
              {...register("end_at")}
            />
            {errors.end_at && (
              <p className={ERROR_CLASS} data-testid="promotion-create-end-error">
                {t(errors.end_at.message ?? "builder.err_dates")}
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="promotion-rule-mode" className={LABEL_CLASS}>
            {t("create.rule_mode_label")}
          </label>
          <select
            id="promotion-rule-mode"
            data-testid="promotion-create-rule-mode"
            disabled={isPending}
            className={FIELD_CLASS}
            {...register("rule_mode")}
          >
            <option value="all">{t("detail.rule_mode.stack_all")}</option>
            <option value="break">{t("detail.rule_mode.first_match")}</option>
          </select>
        </div>
      </form>
    </Modal>
  );
}
